import {
  DeviceCommandRequest,
  Port,
  RelayState,
  RelayStatus,
  RelayStatusDto,
} from '@thingslog/repositories';
import clsx from 'clsx';
import React, { FC, useMemo, useCallback } from 'react';
import { Panel, PanelTitle, RelayButtonGroup } from './base';
import { useTranslation } from 'react-i18next';
import { hash } from '@thingslog/ui-components';
import { Button } from '@mui/material';
import { Link, Refresh } from '@mui/icons-material';

export const DeviceControl: FC<Props> = ({
  className,
  isLoadingRelayStatuses,
  isLoadingLinkedDevices,
  showPollLinkedDevices,
  ports,
  relayStatuses,
  onRelayStateChange,
  onSendCommandClick,
  onPollLinkedDevicesClick,
}: Props) => {
  const { t } = useTranslation();

  const relayStatusesArr: RelayStatus[] = useMemo(() => {
    if (relayStatuses === undefined) return [];
    return relayStatuses.relayStatuses.sort(
      (a: RelayStatus, b: RelayStatus) => a.sensorIndex - b.sensorIndex
    );
  }, [relayStatuses]);

  const showRelayControls = useMemo(() => {
    return relayStatusesArr.length > 0;
  }, [relayStatusesArr]);

  const handleRelayStateChange = useCallback(
    (sensorIndex: number, status: RelayState) => {
      if (onRelayStateChange !== undefined) {
        onRelayStateChange(sensorIndex, status);
      }
    },
    [onRelayStateChange]
  );

  const handleSendCommand = useCallback(
    (commandRequest: DeviceCommandRequest) => {
      if (onSendCommandClick !== undefined) {
        onSendCommandClick(commandRequest);
      }
    },
    [onSendCommandClick]
  );

  const handlePollLinkedDevicesClick = useCallback((): void => {
    if (onPollLinkedDevicesClick !== undefined) {
      onPollLinkedDevicesClick();
    }
  }, [onPollLinkedDevicesClick]);

  if (!showRelayControls && !showPollLinkedDevices) {
    return null;
  }

  return (
    <Panel className={clsx('space-y-2.5', className)}>
      <PanelTitle title={t('control_widget_header')} />
      <section className="flex flex-col-reverse gap-3 lg:flex-row lg:items-center">
        <div className="flex flex-1 flex-col gap-3 lg:flex-row lg:flex-wrap lg:gap-x-2.5 lg:gap-y-3">
          {showRelayControls &&
            relayStatusesArr.map((status: RelayStatus) => {
              return (
                <RelayButtonGroup
                  key={hash(`relay-${status.sensorIndex}`)}
                  status={status}
                  onChange={handleRelayStateChange}
                  ports={ports}
                  isDisabled={isLoadingRelayStatuses}
                />
              );
            })}
        </div>
        <div className="flex w-full flex-shrink-0 flex-col gap-2 lg:w-auto">
          {showRelayControls && (
            <Button
              disabled={isLoadingRelayStatuses}
              variant="contained"
              onClick={(): void => {
                handleSendCommand({
                  commandParameters: {},
                  commandType: 'RELAY_STATUSES',
                });
              }}
              startIcon={<Refresh />}
            >
              {t('relay_control_get_relay_statuses')}
            </Button>
          )}
          {showPollLinkedDevices && (
            <Button
              disabled={isLoadingLinkedDevices}
              variant="outlined"
              onClick={handlePollLinkedDevicesClick}
              startIcon={<Link />}
            >
              {t('poll_linked_devices')}
            </Button>
          )}
        </div>
      </section>
    </Panel>
  );
};

interface Props {
  ports?: Record<number, Port>;
  relayStatuses?: RelayStatusDto;
  showPollLinkedDevices: boolean;
  isLoadingRelayStatuses: boolean;
  isLoadingLinkedDevices: boolean;
  onRelayStateChange?: (sensorIndex: number, status: RelayState) => void;
  onSendCommandClick?: (deviceCommandRequest: DeviceCommandRequest) => void;
  onPollLinkedDevicesClick?: () => void;
  className?: string;
}
